import { useState } from 'react';
import { Link2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { useNotification } from '@/context/NotificationProvider';
import { notifyMutationError } from '@/lib/errors';
import { useTemplateVersions, useUpdateBinding } from '@/hooks/useTemplates';
import type { TemplateFinalidadeResponse, TemplateListItem } from '@/api/templates';

const EMAIL_CATEGORIAS = ['EMAIL', 'EMAIL_LAYOUT'];
const IA_CATEGORIAS = ['PROMPT', 'PROMPT_SYSTEM'];

interface BindingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  finalidade: TemplateFinalidadeResponse | null;
  templates: TemplateListItem[];
}

/**
 * Dialog para vincular um template+versão a uma finalidade.
 *
 * Usar com key={finalidade.nome} no parent para resetar estado
 * quando a finalidade muda.
 */
export function BindingDialog({ open, onOpenChange, finalidade, templates }: BindingDialogProps) {
  const { showSuccess, showError } = useNotification();
  const updateBinding = useUpdateBinding();

  // Estado local — inicializado a partir do binding atual (resetado via key no parent)
  const [templateId, setTemplateId] = useState(finalidade?.binding_atual?.template_id ?? '');
  const [versaoId, setVersaoId] = useState(finalidade?.binding_atual?.versao_id ?? '');

  // Filtra templates pelo domínio da finalidade
  const templatesFiltrados = (() => {
    if (!finalidade) return [];
    const domainCats = EMAIL_CATEGORIAS.includes(finalidade.categoria) ? EMAIL_CATEGORIAS : IA_CATEGORIAS;
    return templates.filter((t) => domainCats.includes(t.categoria));
  })();

  // Carrega versões quando template é selecionado
  const { data: versoes, isLoading: loadingVersoes } = useTemplateVersions(templateId);

  // Se versaoId está vazio (template mudou), auto-seleciona a versão atual
  const effectiveVersaoId = versaoId || (versoes?.find((v) => v.is_current)?.id ?? '');

  const handleTemplateChange = (newId: string) => {
    setTemplateId(newId);
    setVersaoId('');
  };

  const handleSave = () => {
    if (!finalidade || !effectiveVersaoId) return;
    updateBinding.mutate(
      { nome: finalidade.nome, versaoId: effectiveVersaoId },
      {
        onSuccess: () => {
          showSuccess('Vínculo atualizado');
          onOpenChange(false);
        },
        onError: (err) => notifyMutationError(err, showError),
      },
    );
  };

  if (!finalidade) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            Vincular Template
          </DialogTitle>
          <DialogDescription>
            Selecione o template e versão para: <strong>{finalidade.nome}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Template</Label>
            <Select value={templateId} onValueChange={handleTemplateChange}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione um template..." />
              </SelectTrigger>
              <SelectContent>
                {templatesFiltrados.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Versão</Label>
            <Select
              value={effectiveVersaoId}
              onValueChange={setVersaoId}
              disabled={!templateId || loadingVersoes}
            >
              <SelectTrigger>
                <SelectValue placeholder={
                  loadingVersoes ? 'Carregando versões...'
                    : !templateId ? 'Selecione um template primeiro'
                    : 'Selecione uma versão...'
                } />
              </SelectTrigger>
              <SelectContent>
                {(versoes ?? []).map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    v{v.versao}{v.is_current ? ' (atual)' : ''} — {v.created_by_nome ?? 'Sistema'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={updateBinding.isPending}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={updateBinding.isPending || !effectiveVersaoId}>
            {updateBinding.isPending && <Spinner className="mr-2 text-inherit" />}
            Confirmar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
